import {call, delay, put, select, takeEvery, takeLatest} from 'redux-saga/effects'

import history from '../history'

import {closeSlidingModal} from '../actions/modal'
import {
  ADD_PARTICIPANT_TO_ORDER,
  CANCEL_PARTICIPANT_ORDER,
  EDIT_PARTICIPANT,
  GET_EVENT_AND_ORDER_DETAILS,
  GET_EVENT_AND_ORDER_DETAILS_PENDING,
  GET_EVENT_AND_ORDER_DETAILS_SUCCESS,
  GET_EVENT_AND_ORDER_DETAILS_FAILURE,
  HIDE_SUCCESS_MESSAGE_BAR,
  SEND_PARTICIPANT_REMINDER,
  SHOW_SUCCESS_MESSAGE_BAR,
  cancelParticipantOrderSuccess,
  editParticipantSuccess,
  startFormProcessing,
  stopFormProcessing,
} from '../actions/eventMgr'
import {getOrders} from '../actions/postCheckout'
import {closeConfirmModal, openErrorModal} from '../actions/userInteractions'

import {isBadResponse} from '../api/statusCodes'
import * as URLS from '../urls'
import * as api from '../api/api'
import * as eventMgr from '../store/eventMgr'
import * as user from '../store/user'
import * as userInteractionsActions from '../actions/userInteractions'

// Add Participant to Order
// ----------------------------------------------------------------------------

function* addParticipantToOrderWorker({payload}) {
  try {
    const state = yield select()
    const eventId = eventMgr.eventId(state)

    yield put(startFormProcessing())
    yield api.addParticipant(payload).then(
      api.onStatus({
        OK: ({json}) => json,
        DEFAULT: ({statusText}) => {
          throw new Error(`Could not add participant to order: ${statusText}`)
        },
      }),
    )
    yield put({type: GET_EVENT_AND_ORDER_DETAILS, payload: {eventId}})
    yield put(stopFormProcessing())
    yield put(closeSlidingModal())
  } catch (e) {
    console.error(`Could not add participant to order. Reason: ${e.message}`)
    yield put(stopFormProcessing())
    yield put(closeSlidingModal())
    yield put(openErrorModal())
  }
}

export function* addParticipantToOrderSaga() {
  yield takeEvery(ADD_PARTICIPANT_TO_ORDER, addParticipantToOrderWorker)
}

// Cancel Participant Order
// ----------------------------------------------------------------------------

export function* cancelParticipantOrderWorker({payload: {orderId}}) {
  try {
    const state = yield select()
    const data = {
      email: eventMgr.emailAddressByOrderId(state, orderId),
      eventLook: eventMgr.lookIdByParticipantId(state, orderId),
      userId: eventMgr.userIdByOrderId(state, orderId),
    }

    const {status, statusText} = yield call(api.cancelParticipantOrder, data)

    if (isBadResponse(status)) {
      throw new Error(`Could not edit participant: ${statusText}`)
    }
    yield put(cancelParticipantOrderSuccess(data))
    const eventId = yield select(eventMgr.eventId)
    yield call(history.push, `${URLS.EVENT}/${eventId}`)
  } catch (e) {
    console.error(`Could not cancel participant order. Reason: ${e.message}`)
    yield put(userInteractionsActions.closeModal())
    yield put(openErrorModal())
  }
}

export function* cancelParticipantOrderSaga() {
  yield takeLatest(CANCEL_PARTICIPANT_ORDER, cancelParticipantOrderWorker)
}

// Edit Participant
// ----------------------------------------------------------------------------

export function* editParticipantWorker({payload}) {
  yield put(startFormProcessing())

  try {
    // we are essentially removing and adding a participant here
    const {json, status, statusText} = yield call(api.editParticipant, payload)
    if (isBadResponse(status)) {
      throw new Error(`Could not edit participant: ${statusText}`)
    }
    yield put(stopFormProcessing())
    yield put(closeSlidingModal())
    // pass in the new participant data and the old order id so we can find and update the order
    yield put(
      editParticipantSuccess({
        newParticipant: json,
        prevLookId: payload.eventLook,
        prevOrderId: payload.orderId,
        prevUserId: payload.userId,
      }),
    )
    // reroute to the new event order page with the updated participant data
    const eventId = yield select(eventMgr.eventId)
    yield call(history.push, `${URLS.EVENT}/${eventId}/order/${json.orderId}`)
  } catch (e) {
    console.error(`Could not update participant email. Reason: ${e.message}`)
    yield put(stopFormProcessing())
    yield put(closeSlidingModal())
    yield put(openErrorModal())
  }
}

export function* editParticipantSaga() {
  yield takeEvery(EDIT_PARTICIPANT, editParticipantWorker)
}

// Get Event And Order Details
// ----------------------------------------------------------------------------

export function* getEventAndOrderDetailsWorker({payload}) {
  const {eventId} = payload
  try {
    yield put({type: GET_EVENT_AND_ORDER_DETAILS_PENDING})

    const response = yield call(api.getEventAndOrderDetails, eventId)
    const eventOrders = api.onStatus({
      OK: ({json}) => json,
      DEFAULT: ({statusText}) => {
        throw new Error(`Could not get event and order details: ${statusText}`)
      },
    })(response)

    yield put({type: GET_EVENT_AND_ORDER_DETAILS_SUCCESS, payload: eventOrders})

    const userId = yield select(user.id)
    const orderId = yield select(eventMgr.orderIdByUserId, userId)

    // don't dispatch getOrders action if there is no valid orderId
    if (orderId) {
      yield put(getOrders(orderId))
    }
  } catch (e) {
    yield put({type: GET_EVENT_AND_ORDER_DETAILS_FAILURE, error: true, payload: e})
  }
}

export function* getEventAndOrderDetailsSaga() {
  yield takeEvery(GET_EVENT_AND_ORDER_DETAILS, getEventAndOrderDetailsWorker)
}

// Send Participant Reminder Email
// ----------------------------------------------------------------------------

function* sendParticipantReminderWorker({payload}) {
  const {callback, orderId} = payload
  const state = yield select()
  try {
    const data = {
      eventId: eventMgr.eventIdByOrderId(state, orderId),
      participantId: eventMgr.userIdByOrderId(state, orderId),
    }

    // create the request and send the email
    const xhr = new XMLHttpRequest()
    const params = `event_id=${data.eventId}&participant_id=${data.participantId}`
    xhr.open('POST', '/api/send-participant-invite/', true)
    xhr.setRequestHeader('Content-type', 'application/x-www-form-urlencoded')
    xhr.send(params)

    yield put(closeConfirmModal())
    yield put({type: SHOW_SUCCESS_MESSAGE_BAR})
    yield put(callback)
    yield delay(5000)
    yield put({type: HIDE_SUCCESS_MESSAGE_BAR})
  } catch (e) {
    console.error(`Could not send participant reminder email. Reason: ${e.message}`)
    yield put(closeConfirmModal())
    yield put(openErrorModal())
  }
}

export function* sendParticipantReminderSaga() {
  yield takeEvery(SEND_PARTICIPANT_REMINDER, sendParticipantReminderWorker)
}
